import React, { useCallback, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import { MultiplierRadioList } from './MultiplierRadioList';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import type { RateMultiplier } from '@/types';
import { AnimatedFormError } from '../../../form/AnimatedFormError';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { cn } from '@/lib/utils';

interface FixedMultiplierFieldProps {
  isFloor: boolean;
  rateMultipliers: RateMultiplier[];
  selectedCode: string | null;
  onSelectionChange: (code: string | null) => void;
  customValue?: number | null;
  onCustomValueChange?: (value: number | null) => void;
  isCustomized?: boolean;
  context?: 'user' | 'client';
  customizedFieldKey: string;
  errorElement?: React.ReactNode;
}

export const FixedMultiplierField: React.FC<FixedMultiplierFieldProps> = ({
  isFloor,
  rateMultipliers,
  selectedCode,
  onSelectionChange,
  customValue,
  onCustomValueChange,
  isCustomized,
  context,
  customizedFieldKey,
  errorElement,
}) => {
  const labels = {
    title: isFloor ? "Guaranteed Rate" : "Starting Rate",
    description: isFloor
      ? "Your pay is brought up to this tier. If you already earn this rate or higher from overtime, no additional surcharge is added."
      : "The shift starts at this rate and progresses through higher tiers from there."
  };

  // For floor mode with custom value support: input + preset badges
  if (isFloor && onCustomValueChange) {
    return (
      <FloorValueField
        rateMultipliers={rateMultipliers}
        customValue={customValue}
        onCustomValueChange={onCustomValueChange}
        selectedCode={selectedCode}
        onSelectionChange={onSelectionChange}
        isCustomized={isCustomized}
        context={context}
        customizedFieldKey={customizedFieldKey}
        labels={labels}
        errorElement={errorElement}
      />
    );
  }

  // For starting rate (offset) mode: keep the existing radio list
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Label className={fieldLabelClass}>{labels.title}</Label>
          <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
        </div>
        <p className={fieldDescriptionClass}>{labels.description}</p>
      </div>
      <div className="flex flex-col items-end w-full">
        <div id={customizedFieldKey} tabIndex={-1} className="w-full">
          <MultiplierRadioList
            multipliers={rateMultipliers}
            selectedCode={selectedCode}
            onSelectionChange={onSelectionChange}
            showThresholds={false}
          />
        </div>
        {errorElement && <AnimatedFormError className="w-full text-right mt-1">{errorElement}</AnimatedFormError>}
      </div>
    </div>
  );
};

// ─── Floor value input with preset badges ────────────────────────────

interface FloorValueFieldProps {
  rateMultipliers: RateMultiplier[];
  customValue?: number | null;
  onCustomValueChange: (value: number | null) => void;
  selectedCode: string | null;
  onSelectionChange: (code: string | null) => void;
  isCustomized?: boolean;
  context?: 'user' | 'client';
  customizedFieldKey: string;
  labels: { title: string; description: string };
  errorElement?: React.ReactNode;
}

const FloorValueField: React.FC<FloorValueFieldProps> = ({
  rateMultipliers,
  customValue,
  onCustomValueChange,
  selectedCode,
  onSelectionChange,
  isCustomized,
  context,
  customizedFieldKey,
  labels,
  errorElement,
}) => {
  const nonStraightTime = rateMultipliers.filter(m => m.code !== 'STRAIGHT_TIME');

  // Custom mode = no preset code selected
  const isCustom = selectedCode == null;

  // Derive displayed value: custom value when custom, else look up from selected code
  const effectiveValue = isCustom
    ? (customValue ?? null)
    : (nonStraightTime.find(m => m.code === selectedCode)?.defaultMultiplier ?? null);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const parsed = parseFloat(val);
    if (val === '' || val.trim() === '') {
      onCustomValueChange(null);
    } else if (!isNaN(parsed) && parsed > 1) {
      onCustomValueChange(parsed);
    }
  }, [onCustomValueChange]);

  const handlePresetClick = useCallback((multiplier: RateMultiplier) => {
    onSelectionChange(multiplier.code);
  }, [onSelectionChange]);

  const customInputRef = useRef<HTMLInputElement>(null);

  const handleCustomClick = useCallback(() => {
    onSelectionChange(null);
    // Focus the input after React re-renders with isCustom=true
    requestAnimationFrame(() => customInputRef.current?.focus());
  }, [onSelectionChange]);

  // Roving tabindex + arrow-key navigation for the radiogroup
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const allButtons = [
    ...nonStraightTime.map(m => ({ kind: 'tier' as const, code: m.code })),
    { kind: 'custom' as const, code: null as string | null },
  ];
  const activeIndex = isCustom
    ? allButtons.length - 1
    : allButtons.findIndex(b => b.code === selectedCode);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (idx + 1) % allButtons.length;
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (idx - 1 + allButtons.length) % allButtons.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = allButtons.length - 1;
    if (next !== null) {
      e.preventDefault();
      const target = allButtons[next];
      if (target.kind === 'tier') onSelectionChange(target.code);
      else onSelectionChange(null);
      buttonRefs.current[next]?.focus();
    }
  // allButtons is derived inline — intentionally not listed to avoid stale-closure churn
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectionChange, nonStraightTime.length]);

  return (
    <div id={customizedFieldKey} tabIndex={-1} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
      {isCustom ? (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 md:hidden">
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <Label className={fieldLabelClass}>{labels.title}</Label>
                <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
              </div>
              <p className={fieldDescriptionClass}>{labels.description}</p>
            </div>
            <NumberInput
              ref={customInputRef}
              value={effectiveValue ?? ''}
              onChange={handleChange}
              unit="×"
              unitPosition="right"
              size="xs"
              minValue={1.01}
              maxValue={9.99}
              placeholder="1.5"
              className="text-right transition-none self-center"
            />
          </div>
          <div className="hidden md:block space-y-1">
            <div className="flex items-center gap-2">
              <Label className={fieldLabelClass}>{labels.title}</Label>
              <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
              <NumberInput
                ref={customInputRef}
                value={effectiveValue ?? ''}
                onChange={handleChange}
                unit="×"
                unitPosition="right"
                size="xs"
                minValue={1.01}
                maxValue={9.99}
                placeholder="1.5"
                className="text-right transition-none ml-auto md:ml-0"
              />
            </div>
            <p className={fieldDescriptionClass}>{labels.description}</p>
          </div>
        </>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className={fieldLabelClass}>{labels.title}</Label>
            <CustomizedFieldIndicator isCustomized={isCustomized} context={context} />
          </div>
          <p className={fieldDescriptionClass}>{labels.description}</p>
        </div>
      )}
      <div className="flex flex-col items-end w-full space-y-2">
        {nonStraightTime.length > 0 && (
          <div
            role="radiogroup"
            aria-label="Guaranteed rate floor tier"
            className="w-full space-y-2"
          >
            {nonStraightTime.map((m, idx) => {
              const isActive = !isCustom && m.code === selectedCode;
              return (
                <button
                  key={m.code}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-label={`${m.name} (${m.defaultMultiplier != null ? `${m.defaultMultiplier}x` : '—'})`}
                  tabIndex={idx === activeIndex ? 0 : -1}
                  ref={(el) => { buttonRefs.current[idx] = el; }}
                  onClick={() => handlePresetClick(m)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  className={cn(
                    'grid w-full min-h-11 grid-cols-[56px_1fr_auto] items-center gap-2 px-3 py-2 rounded border transition-colors',
                    isActive
                      ? 'border-primary/45 bg-primary/[0.08]'
                      : 'border-border/60 bg-surface-1 dark:bg-transparent hover:border-primary/30',
                  )}
                >
                  <span className={cn(
                    'font-mono text-sm font-bold tabular-nums text-left',
                    isActive ? 'text-primary-text' : 'text-muted-foreground',
                  )}>
                    {m.defaultMultiplier != null ? `${m.defaultMultiplier}x` : '—'}
                  </span>
                  <span className={cn(
                    'font-heading text-[13px] uppercase tracking-wide truncate text-left',
                    isActive ? 'text-foreground' : 'text-muted-foreground',
                  )}>
                    {m.name}
                  </span>
                  {isActive ? (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary">
                      <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    </span>
                  ) : (
                    <span className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                  )}
                </button>
              );
            })}
            {/* Custom tier */}
            {(() => {
              const customIdx = nonStraightTime.length;
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isCustom}
                  aria-label="Custom"
                  tabIndex={customIdx === activeIndex ? 0 : -1}
                  ref={(el) => { buttonRefs.current[customIdx] = el; }}
                  onClick={handleCustomClick}
                  onKeyDown={(e) => handleKeyDown(e, customIdx)}
                  className={cn(
                    'grid w-full min-h-11 grid-cols-[56px_1fr_auto] items-center gap-2 px-3 py-2 rounded border transition-colors',
                    isCustom
                      ? 'border-primary/45 bg-primary/[0.08]'
                      : 'border-border/60 bg-surface-1 dark:bg-transparent hover:border-primary/30',
                  )}
                >
                  <span className={cn(
                    'font-mono text-sm font-bold tabular-nums text-left',
                    isCustom ? 'text-primary-text' : 'text-muted-foreground',
                  )}>
                    {isCustom && effectiveValue != null ? `${effectiveValue}x` : '—'}
                  </span>
                  <span className={cn(
                    'font-heading text-[13px] uppercase tracking-wide truncate text-left',
                    isCustom ? 'text-foreground' : 'text-muted-foreground',
                  )}>
                    Custom
                  </span>
                  {isCustom ? (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary">
                      <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                    </span>
                  ) : (
                    <span className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                  )}
                </button>
              );
            })()}
          </div>
        )}
        {errorElement && <AnimatedFormError className="w-full text-right mt-1">{errorElement}</AnimatedFormError>}
      </div>
    </div>
  );
};
